import { useEffect, useMemo, useState } from "react";
import {
  Activity, Thermometer, Package, Timer, Users, Wrench, Radio,
} from "lucide-react";
import { useI18n } from "@/lib/i18n";

// ---------- localized copy ---------- //
const COPY = {
  de: {
    badge: "LIVE · WERKSTATT MELS",
    overline: "— Live-Werkstatt",
    title_1: "Was gerade läuft.",
    title_2: "Live aus Mels.",
    intro:
      "Ein Blick in unsere Produktion — Bad-Temperaturen, aktive Chargen und freie Kapazität. Aktualisiert sich alle 2 Sekunden.",
    labels: {
      baths: "Aktive Bäder",
      jobs: "Aufträge in Bearbeitung",
      operators: "Team vor Ort",
      capacity: "Kapazität heute",
      pickup: "Nächste Abholung",
      throughput: "Heute veredelt",
    },
    bath_label: "Bad",
    next_slot: "Nächster freier Slot",
    status_running: "läuft",
    status_ready: "bereit",
    status_pending: "wartet",
    ticker_prefix: "LIVE-DATENSTROM",
    demo_hint:
      "Realtime-Simulation zu Demonstrationszwecken. Für echte MES/SPS-Anbindung sprechen Sie uns an.",
  },
  en: {
    badge: "LIVE · MELS SHOP FLOOR",
    overline: "— Live shop floor",
    title_1: "What's running.",
    title_2: "Live from Mels.",
    intro:
      "A look into our production — bath temperatures, active batches and free capacity. Refreshes every 2 seconds.",
    labels: {
      baths: "Active baths",
      jobs: "Jobs in progress",
      operators: "Team on-site",
      capacity: "Capacity today",
      pickup: "Next pickup",
      throughput: "Finished today",
    },
    bath_label: "Bath",
    next_slot: "Next free slot",
    status_running: "running",
    status_ready: "ready",
    status_pending: "queued",
    ticker_prefix: "LIVE DATA STREAM",
    demo_hint:
      "Realtime simulation for demo purposes. Talk to us for a real MES/PLC connection.",
  },
  fr: {
    badge: "LIVE · ATELIER MELS",
    overline: "— Atelier en direct",
    title_1: "Ce qui tourne.",
    title_2: "En direct de Mels.",
    intro:
      "Un aperçu de notre production — températures des bains, lots actifs, capacité libre. Actualisé toutes les 2 secondes.",
    labels: {
      baths: "Bains actifs",
      jobs: "Ordres en cours",
      operators: "Équipe sur site",
      capacity: "Capacité du jour",
      pickup: "Prochain enlèvement",
      throughput: "Traité aujourd'hui",
    },
    bath_label: "Bain",
    next_slot: "Prochain créneau libre",
    status_running: "en marche",
    status_ready: "prêt",
    status_pending: "en attente",
    ticker_prefix: "FLUX DE DONNÉES LIVE",
    demo_hint:
      "Simulation temps réel à titre de démonstration. Nous consulter pour une connexion MES/API réelle.",
  },
  it: {
    badge: "LIVE · OFFICINA MELS",
    overline: "— Officina in tempo reale",
    title_1: "Cosa sta girando.",
    title_2: "Live da Mels.",
    intro:
      "Uno sguardo sulla nostra produzione — temperature bagni, lotti attivi e capacità disponibile. Aggiornato ogni 2 secondi.",
    labels: {
      baths: "Bagni attivi",
      jobs: "Lavorazioni in corso",
      operators: "Team in sede",
      capacity: "Capacità oggi",
      pickup: "Prossimo ritiro",
      throughput: "Trattato oggi",
    },
    bath_label: "Bagno",
    next_slot: "Prossimo slot libero",
    status_running: "in marcia",
    status_ready: "pronto",
    status_pending: "in coda",
    ticker_prefix: "FLUSSO DATI LIVE",
    demo_hint:
      "Simulazione realtime a scopo dimostrativo. Contattateci per un collegamento MES/PLC reale.",
  },
};

// Baths with typical target temperatures (°C)
const BATHS_TEMPLATE = [
  { id: 1, name: "Verzinken 1",  target: 22, tol: 1.5 },
  { id: 2, name: "Verzinken 2",  target: 22, tol: 1.5 },
  { id: 3, name: "Zink-Nickel",  target: 30, tol: 2.0 },
  { id: 4, name: "Trommel",       target: 24, tol: 1.5 },
  { id: 5, name: "CNS-Beize",    target: 45, tol: 3.0 },
  { id: 6, name: "Elektropol.",  target: 65, tol: 3.5 },
  { id: 7, name: "Chem. Nickel", target: 88, tol: 2.5 },
  { id: 8, name: "Passivierung", target: 20, tol: 1.0 },
];

const JOB_CUSTOMERS = [
  "Auftrag SG-2419",
  "Auftrag SG-2420",
  "Auftrag SG-2421",
  "Auftrag SG-2422",
  "Auftrag SG-2423",
  "Auftrag SG-2424",
  "Auftrag SG-2425",
];
const JOB_TYPES = [
  "Verzinken · Rack",
  "Zink-Nickel · Rack",
  "Trommel · Kleinteile",
  "CNS · Elektropolieren",
  "Passivierung",
  "Chemisch-Nickel",
];

// Deterministic hash-based pseudo-random so re-renders don't jitter
function noise(seed) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

// Business-day / hour aware capacity
function currentCapacityPct(nowMs) {
  const d = new Date(nowMs);
  const day = d.getDay(); // 0 = Sun
  const hour = d.getHours() + d.getMinutes() / 60;
  if (day === 0 || day === 6) return 12; // weekend low
  // Mon-Fri: 7-17 h peak
  if (hour < 7 || hour > 17) return 18;
  // sinusoidal peak at 11h
  const peak = 88 - Math.abs(hour - 11) * 4;
  return Math.max(48, Math.min(96, Math.round(peak)));
}

function nextFreeSlot(nowMs) {
  const d = new Date(nowMs);
  // Round up to next 30min, business hours, skip weekends
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() < 30 ? 30 : 60, 0, 0);
  while (d.getDay() === 0 || d.getDay() === 6 || d.getHours() < 7 || d.getHours() >= 17) {
    if (d.getHours() >= 17) {
      d.setDate(d.getDate() + 1);
      d.setHours(7, 30, 0, 0);
    } else {
      d.setHours(7, 30, 0, 0);
    }
    if (d.getDay() === 0) d.setDate(d.getDate() + 1);
    if (d.getDay() === 6) d.setDate(d.getDate() + 2);
  }
  return d;
}

export const LiveWerkstatt = () => {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;

  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 2000);
    return () => clearInterval(id);
  }, []);

  const { baths, jobs, kpis } = useMemo(() => {
    const now = Date.now() + tick * 2000;
    const baths = BATHS_TEMPLATE.map((b, i) => {
      const drift = (noise(now / 30000 + i) - 0.5) * b.tol * 2;
      const temp = +(b.target + drift).toFixed(1);
      const rn = noise(now / 40000 + i * 3);
      const status = rn > 0.75 ? "pending" : rn > 0.1 ? "running" : "ready";
      return { ...b, temp, status };
    });

    const jobs = Array.from({ length: 4 }).map((_, i) => {
      const idx = Math.floor(noise(now / 20000 + i * 7) * JOB_CUSTOMERS.length);
      const typeIdx = Math.floor(noise(now / 22000 + i * 11) * JOB_TYPES.length);
      const progress = Math.round(noise(now / 15000 + i * 13) * 95 + 3);
      const eta = Math.max(5, Math.round(noise(now / 18000 + i * 17) * 180));
      return {
        id: JOB_CUSTOMERS[idx],
        type: JOB_TYPES[typeIdx],
        progress,
        eta,
      };
    });

    const activeBaths = baths.filter((b) => b.status !== "pending").length;
    const capacity = currentCapacityPct(now);
    const throughput = 240 + Math.round(noise(now / 60000) * 40);
    const operators = 4 + Math.round(noise(now / 90000) * 2);
    const jobsInProgress = 6 + Math.round(noise(now / 45000) * 3);

    return {
      baths,
      jobs,
      kpis: { activeBaths, capacity, throughput, operators, jobsInProgress },
    };
  }, [tick]);

  const slot = nextFreeSlot(Date.now());
  const slotStr = slot.toLocaleString(lang === "de" ? "de-CH" : lang, {
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

  const nowStr = new Date().toLocaleTimeString(lang === "de" ? "de-CH" : lang, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  return (
    <section
      id="live"
      data-testid="live-werkstatt-section"
      className="relative bg-[#0B0B0D] py-24 md:py-36 border-t border-white/5 overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-0 opacity-30" style={{
        backgroundImage: "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
        backgroundSize: "40px 40px",
      }} />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-7">
            <div className="overline reveal mb-5 flex items-center gap-3">
              <span className="inline-flex items-center gap-2 px-2 py-1 border border-emerald-400/30 text-[10px] text-emerald-300 bg-emerald-400/5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" />
                {c.badge}
              </span>
              <span>{c.overline}</span>
            </div>
            <h2 className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-[64px] font-semibold tracking-tight leading-[0.95] text-white">
              {c.title_1}
              <br />
              <span className="text-zinc-500">{c.title_2}</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end justify-between gap-6">
            <p className="reveal reveal-delay-2 text-zinc-400 text-base md:text-lg leading-relaxed max-w-md">
              {c.intro}
            </p>
            <div className="flex-none text-right">
              <div className="overline mb-1 flex items-center gap-1.5 justify-end">
                <Radio className="w-3 h-3" /> {c.ticker_prefix}
              </div>
              <div data-testid="live-clock" className="font-mono text-white tabular-nums text-sm">
                {nowStr}
              </div>
            </div>
          </div>
        </div>

        {/* KPI Strip */}
        <div className="reveal grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border border-white/10 mb-8">
          {[
            { icon: Thermometer, label: c.labels.baths,     value: `${kpis.activeBaths}/8` },
            { icon: Package,      label: c.labels.jobs,      value: kpis.jobsInProgress },
            { icon: Users,        label: c.labels.operators, value: kpis.operators },
            { icon: Activity,     label: c.labels.capacity,  value: `${kpis.capacity} %` },
            { icon: Wrench,       label: c.labels.throughput, value: `${kpis.throughput} kg` },
          ].map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.label} className="bg-[#121214] p-4 md:p-5">
                <div className="overline mb-2 flex items-center gap-1.5">
                  <Icon className="w-3 h-3" /> {k.label}
                </div>
                <div className="font-display text-2xl md:text-3xl text-white tabular-nums">
                  {k.value}
                </div>
              </div>
            );
          })}
        </div>

        {/* Grid: baths + jobs */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Baths */}
          <div className="lg:col-span-7">
            <div className="overline mb-4 flex items-center gap-2">
              <Thermometer className="w-3.5 h-3.5" /> {c.labels.baths}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
              {baths.map((b) => {
                const delta = b.temp - b.target;
                const inSpec = Math.abs(delta) <= b.tol;
                const dotColor =
                  b.status === "running" ? "bg-emerald-400" :
                  b.status === "ready" ? "bg-amber-300" : "bg-zinc-500";
                return (
                  <div key={b.id} data-testid={`bath-${b.id}`} className="bg-[#121214] p-4">
                    <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                      <span className={`w-1.5 h-1.5 rounded-full ${dotColor} pulse-dot`} />
                      {c.bath_label} {b.id}
                    </div>
                    <div className="text-xs text-zinc-400 mb-2 truncate">{b.name}</div>
                    <div className={`font-display font-semibold text-2xl tabular-nums ${inSpec ? "text-white" : "text-amber-300"}`}>
                      {b.temp}
                      <span className="text-xs text-zinc-500 ml-1">°C</span>
                    </div>
                    <div className="mt-2 h-1 bg-white/10">
                      <div
                        className={`h-full ${inSpec ? "bg-emerald-400/70" : "bg-amber-300/70"}`}
                        style={{ width: `${Math.min(100, (b.temp / (b.target * 1.6)) * 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Jobs + pickup */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div>
              <div className="overline mb-4 flex items-center gap-2">
                <Package className="w-3.5 h-3.5" /> {c.labels.jobs}
              </div>
              <div className="border border-white/10 divide-y divide-white/10">
                {jobs.map((j, i) => (
                  <div key={`${j.id}-${i}`} data-testid={`job-${i}`} className="p-3.5 bg-[#121214]">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <div className="text-white text-sm font-medium truncate">{j.id}</div>
                        <div className="text-[11px] text-zinc-500 truncate">{j.type}</div>
                      </div>
                      <div className="text-[11px] text-zinc-400 tabular-nums whitespace-nowrap">
                        <Timer className="w-3 h-3 inline mr-1" />
                        {j.eta} min
                      </div>
                    </div>
                    <div className="relative h-1 bg-white/10">
                      <div
                        className="absolute inset-y-0 left-0 bg-emerald-400/80 transition-all duration-1000"
                        style={{ width: `${j.progress}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border border-white/10 p-5 bg-gradient-to-br from-[#0E0E10] to-[#141416]">
              <div className="overline mb-2 flex items-center gap-1.5">
                <Timer className="w-3 h-3" /> {c.next_slot}
              </div>
              <div data-testid="next-slot" className="font-display text-2xl text-white tabular-nums">
                {slotStr}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 text-[11px] text-zinc-500">{c.demo_hint}</div>
      </div>
    </section>
  );
};

export default LiveWerkstatt;
